import { useEffect, useRef } from "react";
import { X, Trash2 } from "lucide-react";
import type { PdfAnnotation } from "./types";

type Props = {
  annotation: PdfAnnotation;
  anchorClientRect: DOMRect;
  onClose: () => void;
  onDelete: () => void;
};

export function PdfInlinePopover({
  annotation,
  anchorClientRect,
  onClose,
  onDelete,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const popoverWidth = 380;
    const popoverHeight = el.offsetHeight || 200;

    // Position below anchor by default
    let top = anchorClientRect.bottom + 8;
    // If not enough space below, position above
    if (top + popoverHeight > window.innerHeight - 8) {
      top = anchorClientRect.top - popoverHeight - 8;
    }

    let left = anchorClientRect.left;
    // Keep within viewport
    left = Math.min(window.innerWidth - popoverWidth - 8, Math.max(8, left));

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [anchorClientRect]);

  return (
    <div
      ref={ref}
      className="fixed z-50 w-[380px] rounded-xl border bg-background shadow-lg"
      role="dialog"
      aria-label="PDF annotation"
    >
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="text-sm font-medium capitalize">{annotation.type}</div>
        <div className="flex items-center gap-1">
          <button
            className="rounded p-1 text-destructive hover:bg-destructive/10 transition-colors"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            className="rounded p-1 text-muted-foreground hover:bg-muted transition-colors"
            onClick={onClose}
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2 px-3 py-3">
        <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground line-clamp-3">
          "{annotation.text}"
        </div>

        {annotation.payload.aiResponse && (
          <div className="text-sm leading-relaxed">{annotation.payload.aiResponse}</div>
        )}

        {annotation.payload.noteText && (
          <div className="text-sm leading-relaxed">{annotation.payload.noteText}</div>
        )}

        {annotation.payload.question && (
          <div className="text-xs text-muted-foreground">
            Q: {annotation.payload.question}
          </div>
        )}
      </div>
    </div>
  );
}
